import React from "react"
import QuestionContent from "./QuestionContent"
import { toHtml } from "./MathText"
import { imageAttrs } from "../lib/questionImages"

// Renderiza o ENUNCIADO, que pode ser HTML (do editor Trix no admin) ou texto
// puro legado (motor de extração / importador). Espelha Question.looks_like_html?
// do backend: se tem tag de formatação, trata como HTML; senão delega ao
// QuestionContent (caminho de texto puro, idêntico ao de antes).
//
// No caminho HTML o conteúdo já vem sanitizado do servidor (allowlist no model).
// Aqui só: expande [[IMG:]] e renderiza $...$ (KaTeX) dentro dos nós de texto,
// preservando as tags de formatação.

const IMG_RE = /\[\[IMG:([^\]]+)\]\]/g
// Mesma allowlist de Question::STATEMENT_ALLOWED_TAGS.
const HTML_RE = /<\/?(?:strong|em|b|i|u|s|del|a|h1|h2|h3|p|br|div|ul|ol|li|blockquote|pre|sub|sup)\b[^>]*>/i

function looksLikeHtml(s: string): boolean {
  return HTML_RE.test(s)
}

// [[IMG:nome]] -> <img src="/questoes/nome"> (mesma convenção de QuestionContent).
// encodeURIComponent garante que o nome não escape do atributo src.
function expandImages(html: string): string {
  return html.replace(IMG_RE, (_m, name: string) => {
    const src = `/questoes/${encodeURIComponent(name.trim())}`
    return `<img src="${src}" alt="Figura da questão"${imageAttrs(name)} />`
  })
}

// Aplica escape + KaTeX (toHtml) em cada nó de texto do HTML sanitizado,
// mantendo intactas as tags de formatação. DOMParser/TreeWalker rodam no browser.
function renderRichHtml(html: string): string {
  const doc = new DOMParser().parseFromString(expandImages(html), "text/html")
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text)
  for (const node of textNodes) {
    const span = doc.createElement("span")
    span.innerHTML = toHtml(node.textContent || "")
    node.replaceWith(span)
  }
  return doc.body.innerHTML
}

interface RichContentProps {
  html: string
  className?: string
}

export default function RichContent({ html, className }: RichContentProps) {
  const raw = html || ""
  // Texto puro legado: preserva quebras de linha via whitespace-pre-line (como antes).
  if (!looksLikeHtml(raw)) {
    return <QuestionContent text={raw} className={["whitespace-pre-line", className].filter(Boolean).join(" ")} />
  }
  // HTML: quebras vêm das tags; pre-line sairia. rich-statement restaura a formatação.
  return (
    <div
      className={["rich-statement", className].filter(Boolean).join(" ")}
      dangerouslySetInnerHTML={{ __html: renderRichHtml(raw) }}
    />
  )
}
